/*
 * Copyright (c) 2024, King Living
 * Store Tile Component - Displays individual store information
 * STORLOC-01: Store listing page tile component
 */

import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import Link from '@salesforce/retail-react-app/app/components/link';

import {
  RegionModalOtherRegionsArrowIcon,
  StoreTileShowroomPinIcon,
  LocationDetailIcon,
  PhoneIcon,
  EmailIcon,
} from '@amblique/king-living/app/components/icons';

import {
  Box,
  Button,
  Flex,
  HStack,
  Link as ChakraLink,
  Text,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {getStoreSlug} from '../../utils/utils';

/**
 * StoreTile Component
 * Displays a single store/showroom tile with contact information and actions
 *
 * @param {Object} props - Component props
 * @param {Object} props.store - Store data object
 * @param {boolean} props.isMyShowroom - Whether this is the user's selected showroom
 * @param {function} props.onSetMyShowroom - Callback when "Set as my showroom" is clicked
 * @param {function} props.onViewDetail - Callback when "View detail" is clicked
 * @returns {React.ReactElement}
 */
export const StoreTile = ({
  store,
  isMyShowroom = false,
  showSetMyShowroom = true,
  onSetMyShowroom,
  onViewDetail,
  ...props
}) => {
  const styles = useMultiStyleConfig('Showrooms', {isMyShowroom});

  if (!store) return null;

  // Build full address string
  const fullAddress = [
    store.address1 || store.address?.address1,
    store.city || store.address?.city,
    store.stateCode || store.address?.state,
    store.postalCode || store.address?.postalCode
  ].filter(Boolean).join(' ');

  const phone = store.phone;
  const email = store.email;
  const storeName = store.name || '';
  // Prefer SFCC Slug (c_slug); fall back to name-derived slug
  const storeSlug = getStoreSlug(store);

  const handleSetMyShowroom = () => {
    if (onSetMyShowroom) {
      onSetMyShowroom(store);
    }
  };

  const handleViewDetail = () => {
    if (onViewDetail) {
      onViewDetail(store);
    }
  };

  return (
    <Box className="store-tile" {...styles.storeTileContainer} {...props} {...(isMyShowroom ? {...styles.storeTileContainerActive} : {})}>
      <VStack {...styles.storeTileContentStack}>
        <Box className="store-tile-content" {...styles.storeTileContentContainer}>
          {/* Store Name */}
          <Text {...styles.storeTileStoreName}>
            {storeName}
          </Text>

          <Box className="store-tile-info" {...styles.storeTileInfoContainer}>
            {/* Address */}
            {fullAddress && (
              <HStack {...styles.storeTileInfoRow}>
                <LocationDetailIcon {...styles.storeTileIcon} />
                <Text {...styles.storeTileInfoText}>
                  {fullAddress}
                </Text>
              </HStack>
            )}

            {/* Phone */}
            {phone && (
              <HStack {...styles.storeTileInfoRow}>
                <PhoneIcon {...styles.storeTileIcon} mt={0} />
                <Text {...styles.storeTileInfoText}>
                  <a href={`tel:${phone}`}>{phone}</a>
                </Text>
              </HStack>
            )}

            {/* Email */}
            {email && (
              <HStack {...styles.storeTileInfoRow}>
                <EmailIcon {...styles.storeTileIcon} mt={0} />
                <Text {...styles.storeTileInfoText}>
                  <a href={`mailto:${email}`}>{email}</a>
                </Text>
              </HStack>
            )}
          </Box>
        </Box>

        {/* Actions Row */}
        <Flex className="store-tile-actions-row" {...styles.storeTileActionsRow}>
          {/* KL-986 — "My showroom" is a TAG, not a CTA: when this is the user's showroom it is
              rendered inert (unclickable, no redirect). "Set as my showroom" stays a functional CTA.
              (Tag visual/styling is handled in a separate ticket.) */}
          {showSetMyShowroom && (
            <Button
              {...(isMyShowroom ? {...styles.storeTileButton, ...styles.storeTileButtonActive} : styles.storeTileButton)}
              leftIcon={<StoreTileShowroomPinIcon height="13px" width="12px" />}
              {...(isMyShowroom
                ? {as: 'div', cursor: 'default', pointerEvents: 'none'}
                : {onClick: handleSetMyShowroom})}
            >
              {isMyShowroom ? (
                <FormattedMessage
                  defaultMessage="My showroom"
                  id="store_tile.button.my_showroom"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Set as my showroom"
                  id="store_tile.button.set_as_my_showroom"
                />
              )}
            </Button>
          )}

          {/* View Detail Link */}
          <ChakraLink
            {...styles.storeTileLink}
            as={Link}
            to={`/showroom/${storeSlug}`}
            onClick={handleViewDetail}
          >
            <HStack {...styles.storeTileLinkRow}>
              <Text>
                <FormattedMessage
                  defaultMessage="View details"
                  id="store_tile.link.view_detail"
                />
              </Text>
              {/* <RegionModalOtherRegionsArrowIcon aria-hidden boxSize="15px" height="13px" width="15px" /> */}
            </HStack>
          </ChakraLink>
        </Flex>
      </VStack>
    </Box>
  );
};

StoreTile.displayName = 'StoreTile';

StoreTile.propTypes = {
  store: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    address1: PropTypes.string,
    city: PropTypes.string,
    stateCode: PropTypes.string,
    postalCode: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    c_slug: PropTypes.string,
    c_urlSlug: PropTypes.string,
    slug: PropTypes.string,
    address: PropTypes.shape({
      address1: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      postalCode: PropTypes.string,
    }),
  }),
  isMyShowroom: PropTypes.bool,
  showSetMyShowroom: PropTypes.bool,
  onSetMyShowroom: PropTypes.func,
  onViewDetail: PropTypes.func,
};

export default StoreTile;
